import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useLazyCommand, useEvent } from '@soap/modules';
import { ArrowLeft, FileText, GraduationCap, ChevronDown, ChevronRight, CheckCircle2, Circle, Clock, Award } from 'lucide-react';
import { CurriculumHeader } from '../../components/CurriculumHeader';
import { Button } from '../../ui/button';
import { StudentSelector } from '../../components/StudentSelector';
import { useSelectedStudent } from '../../hooks/useSelectedStudent';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader } from '../../ui/card';

import { useFormatDisplayDate } from '../../../utils/useFormatDisplayDate';

type SubjectDto = {
  e292_SubjectName?: string;
  e292_Topics?: TopicDto[];
};

type TopicDto = {
  e292_TopicId?: string | null;
  e292_TopicName?: string;
  e292_Units?: UnitDto[];
};

type UnitDto = {
  e292_UnitId?: string;
  e292_UnitTitle?: string;
  e292_CompletionDate?: string | null;
  e292_DurationInHours?: number | null;
  e292_Lessons?: LessonDto[];
  e292_LearningObjectives?: ObjectiveDto[];
  e292_AssessmentScore?: number | null;
  e292_AssessmentCompletedAt?: string | null;
};

type LessonDto = {
  e292_LessonId?: string;
  e292_LessonTitle?: string;
  e292_CompletionDate?: string | null;
  e292_DurationInHours?: number;
  e292_State?: string;
};

type ObjectiveDto = {
  e292_Objective?: string;
  e292_Met?: boolean;
};

const VIEW = { idle: 0, loading: 1, ready: 2, noStudent: 3, noData: 4 };

function formatDuration(hours: number | null | undefined): string {
  if (!hours && hours !== 0) return 'N/A';
  if (hours === 0) return '0 hours';
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  if (minutes === 0) {
    return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
  }
  return `${wholeHours}h ${minutes}m`;
}

function getAssessmentScoreColor(score: number | null | undefined): string {
  if (!score && score !== 0) return 'text-gray-500';
  if (score >= 50) return 'text-confirm-action';
  if (score >= 40) return 'text-amber-600';
  return 'text-destructive-action';
}

export function LearningActivityAuditReportPage() {
  const formatDisplayDate = useFormatDisplayDate();
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not completed';
    const formatted = formatDisplayDate(dateString);
    return formatted || 'Invalid date';
  };
  const { selectedStudentId, students, setSelectedStudent, isLoading: isStudentLoading } = useSelectedStudent();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<string>(String(currentYear));
  const [reportData, setReportData] = useState<SubjectDto[] | null>(null);
  const [viewState, setViewState] = useState<number>(VIEW.idle);
  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set());
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());

  const [sendGetReport] = useLazyCommand('C292v1_GetLearningActivityAuditData');

  useEvent({
    eventName: 'E292v1_GotLearningActivityAuditData',
    onEventReceived: (event: { e292_Subjects?: SubjectDto[] }) => {
      setReportData(event?.e292_Subjects ?? []);
      setViewState(VIEW.ready);
    },
  });

  useEffect(() => {
    if (isStudentLoading) {
      setViewState(VIEW.loading);
      return;
    }
    if (!selectedStudentId) {
      setReportData(null);
      setViewState(VIEW.noStudent);
      return;
    }
    setViewState(VIEW.loading);
    sendGetReport({
      C292_StudentId: selectedStudentId,
      C292_Year: Number(selectedYear) || currentYear,
      C292_CurriculumProviderKey: 'oak',
    });
  }, [selectedStudentId, selectedYear, isStudentLoading]);

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const toggleSubject = (subjectName: string) => {
    setExpandedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(subjectName)) {
        next.delete(subjectName);
      } else {
        next.add(subjectName);
      }
      return next;
    });
  };

  const toggleTopic = (topicKey: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicKey)) {
        next.delete(topicKey);
      } else {
        next.add(topicKey);
      }
      return next;
    });
  };

  const toggleUnit = (unitId: string) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  };

  return (
    <div className="p-4 sm:p-6">
        <CurriculumHeader showTabs={false} title="Learning Activity Audit" icon={FileText} showStudentDropdown={false} />
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <Link to="/app/reports">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft size={16} />
              Back to Reports
            </Button>
          </Link>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="audit-student" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Student
              </Label>
              <StudentSelector
                students={students}
                selectedStudentId={selectedStudentId}
                onSelect={setSelectedStudent}
                showLabel={false}
                placeholder="Select a student"
                className="min-w-[180px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="audit-year" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Year
              </Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger id="audit-year" className="w-[120px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {viewState === VIEW.noStudent && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <GraduationCap className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a student</h3>
            <p className="text-gray-600 mb-4">
              Choose a student from the dropdown above to view their learning activity audit report.
            </p>
            <p className="text-sm text-gray-500">Add students in Profiles if you do not have any yet.</p>
          </div>
        )}

        {viewState === VIEW.loading && (
          <div className="flex justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue" />
              <span className="text-gray-600">Loading…</span>
            </div>
          </div>
        )}

        {viewState === VIEW.ready && reportData && (
          <div className="space-y-4">
            {reportData.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No data for this year</h3>
                <p className="text-gray-600">
                  There are no completed units for this student in the selected year ({selectedYear}).
                </p>
              </div>
            ) : (
              reportData.map((subject) => {
                const subjectName = subject.e292_SubjectName ?? 'Subject';
                const isSubjectExpanded = expandedSubjects.has(subjectName);

                return (
                  <Card key={subjectName} className="border border-gray-200">
                    <CardHeader
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleSubject(subjectName)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isSubjectExpanded ? (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-500" />
                          )}
                          <h3 className="text-lg font-semibold text-gray-900">{subjectName}</h3>
                          <span className="text-sm text-gray-500">
                            ({subject.e292_Topics?.length ?? 0} topic{subject.e292_Topics?.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    {isSubjectExpanded && (
                      <CardContent className="pt-0">
                        <div className="space-y-3 pl-7">
                          {subject.e292_Topics?.map((topic) => {
                            const topicKey = `${subjectName}-${topic.e292_TopicId ?? topic.e292_TopicName}`;
                            const isTopicExpanded = expandedTopics.has(topicKey);
                            const topicName = topic.e292_TopicName ?? 'Topic';

                            return (
                              <Card key={topicKey} className="border border-gray-200">
                                <CardHeader
                                  className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                                  onClick={() => toggleTopic(topicKey)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {isTopicExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-gray-500" />
                                      )}
                                      <h4 className="text-base font-medium text-gray-900">{topicName}</h4>
                                      <span className="text-sm text-gray-500">
                                        ({topic.e292_Units?.length ?? 0} unit{topic.e292_Units?.length !== 1 ? 's' : ''})
                                      </span>
                                    </div>
                                  </div>
                                </CardHeader>
                                {isTopicExpanded && (
                                  <CardContent className="pt-0">
                                    <div className="space-y-3 pl-6">
                                      {topic.e292_Units?.map((unit) => {
                                        const unitId = unit.e292_UnitId ?? '';
                                        const isUnitExpanded = expandedUnits.has(unitId);

                                        return (
                                          <Card key={unitId} className="border border-gray-200 bg-gray-50">
                                            <CardHeader
                                              className="cursor-pointer hover:bg-gray-100 transition-colors py-3"
                                              onClick={() => toggleUnit(unitId)}
                                            >
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  {isUnitExpanded ? (
                                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                                  ) : (
                                                    <ChevronRight className="h-4 w-4 text-gray-500" />
                                                  )}
                                                  <h5 className="text-sm font-semibold text-gray-900">
                                                    {unit.e292_UnitTitle ?? 'Unit'}
                                                  </h5>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-gray-600">
                                                  {unit.e292_CompletionDate && (
                                                    <span>Completed: {formatDate(unit.e292_CompletionDate)}</span>
                                                  )}
                                                  {unit.e292_DurationInHours !== null && unit.e292_DurationInHours !== undefined && (
                                                    <span className="flex items-center gap-1">
                                                      <Clock className="h-3 w-3" />
                                                      {formatDuration(unit.e292_DurationInHours)}
                                                    </span>
                                                  )}
                                                  {unit.e292_AssessmentScore !== null && unit.e292_AssessmentScore !== undefined && (
                                                    <span className={`flex items-center gap-1 font-medium ${getAssessmentScoreColor(unit.e292_AssessmentScore)}`}>
                                                      <Award className="h-3 w-3" />
                                                      {unit.e292_AssessmentScore}%
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </CardHeader>
                                            {isUnitExpanded && (
                                              <CardContent className="pt-0">
                                                <div className="space-y-4 pl-6">
                                                  {/* Learning Objectives */}
                                                  {unit.e292_LearningObjectives && unit.e292_LearningObjectives.length > 0 && (
                                                    <div>
                                                      <h6 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                                        Learning Objectives
                                                      </h6>
                                                      <ul className="space-y-1">
                                                        {unit.e292_LearningObjectives.map((obj, idx) => (
                                                          <li key={idx} className="flex items-start gap-2 text-sm">
                                                            {obj.e292_Met ? (
                                                              <CheckCircle2 className="h-4 w-4 text-confirm-action mt-0.5 flex-shrink-0" />
                                                            ) : (
                                                              <Circle className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                            )}
                                                            <span className={obj.e292_Met ? 'text-gray-900' : 'text-gray-600'}>
                                                              {obj.e292_Objective ?? 'Objective'}
                                                            </span>
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {/* Assessment Details */}
                                                  {unit.e292_AssessmentScore !== null && unit.e292_AssessmentScore !== undefined && (
                                                    <div>
                                                      <h6 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                                        Assessment Results
                                                      </h6>
                                                      <div className="text-sm space-y-1">
                                                        <div className="flex items-center gap-2">
                                                          <span className="text-gray-600">Score:</span>
                                                          <span className={`font-medium ${getAssessmentScoreColor(unit.e292_AssessmentScore)}`}>
                                                            {unit.e292_AssessmentScore}%
                                                          </span>
                                                        </div>
                                                        {unit.e292_AssessmentCompletedAt && (
                                                          <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">Completed:</span>
                                                            <span className="text-gray-900">
                                                              {formatDate(unit.e292_AssessmentCompletedAt)}
                                                            </span>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}

                                                  {/* Lessons */}
                                                  {unit.e292_Lessons && unit.e292_Lessons.length > 0 && (
                                                    <div>
                                                      <h6 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                                        Lessons ({unit.e292_Lessons.length})
                                                      </h6>
                                                      <div className="space-y-2">
                                                        {unit.e292_Lessons.map((lesson) => (
                                                          <div
                                                            key={lesson.e292_LessonId}
                                                            className="bg-white border border-gray-200 rounded p-3 text-sm"
                                                          >
                                                            <div className="flex items-start justify-between">
                                                              <div className="flex-1">
                                                                <div className="font-medium text-gray-900 mb-1">
                                                                  {lesson.e292_LessonTitle ?? 'Lesson'}
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                                                                  {lesson.e292_CompletionDate && (
                                                                    <span>Completed: {formatDate(lesson.e292_CompletionDate)}</span>
                                                                  )}
                                                                  <span className="flex items-center gap-1">
                                                                    <Clock className="h-3 w-3" />
                                                                    {formatDuration(lesson.e292_DurationInHours)}
                                                                  </span>
                                                                  {lesson.e292_State && (
                                                                    <span className="capitalize">{lesson.e292_State.toLowerCase()}</span>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              </CardContent>
                                            )}
                                          </Card>
                                        );
                                      })}
                                    </div>
                                  </CardContent>
                                )}
                              </Card>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
  );
}
